/* global PublicKeyCredential */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  Fingerprint,
  Face,
  Close,
  CheckCircle,
  Error
} from '@mui/icons-material';
import api from '../../services/api';

const BiometricAuth = ({ open, onClose, onSuccess, action = 'clock-in' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricType, setBiometricType] = useState('');

  useEffect(() => {
    checkBiometricSupport();
  }, []);

  const checkBiometricSupport = async () => {
    if ('credentials' in navigator && 'create' in navigator.credentials) {
      // WebAuthn is supported
      setBiometricSupported(true);
      
      // Check if device has biometric capability
      if (window.PublicKeyCredential) {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        if (available) {
          // Detect biometric type based on device
          const userAgent = navigator.userAgent.toLowerCase();
          if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
            setBiometricType('face'); // Face ID
          } else {
            setBiometricType('fingerprint'); // Touch ID or fingerprint
          }
        }
      }
    }
  };

  const handleBiometricAuth = async () => {
    try {
      setLoading(true);
      setError('');

      // Create WebAuthn challenge
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const publicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: "Omega Studio",
          id: window.location.hostname,
        },
        user: {
          id: new TextEncoder().encode(localStorage.getItem('userId') || 'user'),
          name: localStorage.getItem('userEmail') || 'user@example.com',
          displayName: localStorage.getItem('userName') || 'User',
        },
        pubKeyCredParams: [{ alg: -7, type: "public-key" }],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required"
        },
        timeout: 60000,
        attestation: "direct"
      };

      // Request biometric authentication
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      if (credential) {
        // Convert credential to base64 for transmission
        const credentialData = {
          id: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
          type: credential.type,
          biometricType,
          deviceId: navigator.userAgent
        };

        // Send to backend for verification
        const response = await api.post(`/attendance/biometric-${action}`, {
          biometricData: JSON.stringify(credentialData),
          deviceId: localStorage.getItem('deviceId') || generateDeviceId(),
          location: localStorage.getItem('lastLocation') || 'Office',
          latitude: localStorage.getItem('lastLatitude'),
          longitude: localStorage.getItem('lastLongitude')
        });

        onSuccess(response.data);
      }
    } catch (error) {
      console.error('Biometric auth error:', error);
      if (error.name === 'NotAllowedError') {
        setError('Biometric authentication was cancelled or not available');
      } else if (error.name === 'InvalidStateError') {
        setError('This device is already registered');
      } else {
        setError('Biometric authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateDeviceId = () => {
    const deviceId = 'device_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('deviceId', deviceId);
    return deviceId;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Biometric {action === 'clock-in' ? 'Clock In' : 'Clock Out'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box textAlign="center" py={3}>
          {loading ? (
            <>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Please complete biometric authentication...
              </Typography>
            </>
          ) : biometricSupported ? (
            <>
              {biometricType === 'face' ? (
                <Face sx={{ fontSize: 80, color: 'primary.main' }} />
              ) : (
                <Fingerprint sx={{ fontSize: 80, color: 'primary.main' }} />
              )}
              <Typography variant="h6" sx={{ mt: 2 }}>
                Use {biometricType === 'face' ? 'Face ID' : 'Fingerprint'} to {action}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Place your {biometricType === 'face' ? 'face in view' : 'finger on the sensor'}
              </Typography>
            </>
          ) : (
            <>
              <Error sx={{ fontSize: 80, color: 'error.main' }} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Biometric Not Available
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Your device doesn't support biometric authentication
              </Typography>
            </>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {biometricSupported && (
          <Button 
            variant="contained" 
            onClick={handleBiometricAuth}
            disabled={loading}
            startIcon={biometricType === 'face' ? <Face /> : <Fingerprint />}
          >
            Authenticate
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BiometricAuth;
